import React, { useState, useEffect } from 'react';
import { ClockIcon, UserGroupIcon, PlusIcon } from './Icons';

const HomeScreen = ({ studentsOut, onStudentSelect, onSetup, onRefresh, onLogout, onNewSignout, selectedClass, sheetsService }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [signoutCounts, setSignoutCounts] = useState({});
  const [returningIndex, setReturningIndex] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Fetch signout counts when studentsOut or selectedClass changes
  useEffect(() => {
    if (studentsOut.length > 0 && selectedClass && sheetsService) {
      fetchSignoutCounts();
    }
  }, [studentsOut, selectedClass, sheetsService]);

  const fetchSignoutCounts = async () => {
    try {
      const counts = {};
      for (const student of studentsOut) {
        try {
          const count = await sheetsService.getStudentSignoutCount(student.name, selectedClass);
          counts[student.name] = count;
        } catch (error) {
          console.error(`Error fetching count for ${student.name}:`, error);
          counts[student.name] = 0;
        }
      }
      setSignoutCounts(counts);
    } catch (error) {
      console.error('Error fetching signout counts:', error);
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDuration = (startIso) => {
    if (!startIso) return '';
    try {
      const start = new Date(startIso);
      const diffMs = Math.max(0, currentTime - start);
      const totalSeconds = Math.floor(diffMs / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      if (hours > 0) return `${hours}h ${minutes}m`;
      if (minutes > 0) return `${minutes}m ${seconds}s`;
      return `${seconds}s`;
    } catch (e) {
      return '';
    }
  };

  const handleMarkReturned = async (student, index) => {
    if (!sheetsService) return;
    try {
      setReturningIndex(index);
      await sheetsService.recordReturn(student, new Date().toISOString());
      await onRefresh();
    } catch (err) {
      console.error('Error marking return:', err);
      alert('Failed to mark as returned. Please try again.');
    } finally {
      setReturningIndex(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">K12Core Signout System</h1>
              {selectedClass && (
                <p className="text-sm text-gray-600 mt-1">Current Class: {selectedClass}</p>
              )}
              {sheetsService && (
                <div className="mt-2 text-xs text-blue-600">
                  {(() => {
                    try {
                      const tokenData = localStorage.getItem('google_auth_token');
                      if (tokenData) {
                        const parsed = JSON.parse(tokenData);
                        const now = Date.now();
                        const timeLeft = parsed.expires_at - now;
                        const minutesLeft = Math.floor(timeLeft / (1000 * 60));
                        if (minutesLeft > 0) {
                          return `Session active for ${minutesLeft} more minutes`;
                        } else {
                          return 'Session expired - please refresh';
                        }
                      }
                      return 'Session status unknown';
                    } catch (e) {
                      return 'Session status error';
                    }
                  })()}
                </div>
              )}
            </div>
            <div className="flex items-center space-x-3">
              {/* {selectedClass && (
                <button
                  onClick={onNewSignout}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  New Signout
                </button>
              )} */}
              {/* <button
                onClick={onSetup}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
              >
                Setup
              </button>
              <button
                onClick={onLogout}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
              >
                Logout
              </button> */}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-20">
        {!selectedClass ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-blue-600 text-2xl">🎯</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to K12Core!</h2>
            <p className="text-gray-600 mb-6">
              You're all set up! Click the "Setup" button above to select your class and start managing student signouts.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
              <h3 className="font-medium text-blue-900 mb-2">What happens next?</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Click "Setup" to access your class settings</li>
                <li>• Enter the default passcode: <strong>1234</strong></li>
                <li>• Select your class from the available options</li>
                <li>• Start managing student signouts!</li>
              </ul>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Students Currently Out</h2>
              <p className="text-gray-600">Manage student signouts for {selectedClass}</p>
            </div>

            {studentsOut.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-green-600 text-2xl">✅</span>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">All students are present</h3>
                <p className="text-gray-500">No students are currently signed out from {selectedClass}.</p>
                <button
                  onClick={onNewSignout}
                  className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  Start New Signout
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {studentsOut.map((student, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow duration-200"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 font-semibold text-sm">
                            {student.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">{student.name}</h3>
                          <p className="text-sm text-gray-500">{student.className || selectedClass}</p>
                          <p className="text-xs text-gray-500 mt-1">Out for {formatDuration(student.exitTime)}</p>
                          {signoutCounts[student.name] !== undefined && (
                            <p className="text-xs text-blue-600">
                              {signoutCounts[student.name]} signout{signoutCounts[student.name] !== 1 ? 's' : ''} in past 28 days
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleMarkReturned(student, index)}
                          disabled={returningIndex === index}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200 disabled:opacity-50"
                        >
                          {returningIndex === index ? 'Marking...' : 'Mark Returned'}
                        </button>
                        <button
                          onClick={() => onStudentSelect(student)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                        >
                          Details
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {sheetsService && (
              <button
                onClick={() => sheetsService.extendSession()}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Keep Me Logged In
              </button>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onRefresh}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors duration-200"
            >
              Refresh
            </button>
            <button
                onClick={onSetup}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
              >
                Setup
              </button>
              <button
                onClick={onLogout}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
              >
                Logout
              </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeScreen;
